import { useEffect, useState, useRef, type FormEvent } from 'react';
import { DollarSign } from 'lucide-react';
import type { AporteCampaña } from '../lib/supabase';
import { aporteCampanaService } from '../services/aporteCampanaService';
import { comprobanteService } from '../services/comprobanteService';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import type { EntidadDinero, RespaldoMovimiento } from '../types/comprobante';
import { fechaEnChile } from '../utils/dateHelpers';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import { esResultadoCampanaIncierto, importePendienteCampana, sumarImportesCampana } from '../utils/fondosCampanaRpc';
import { formatCurrency } from '../utils/formatters';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { DialogShell } from './ui/DialogShell';
import { TextInput } from './ui/TextInput';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { HistorialPagosCampana } from './HistorialPagosCampana';

interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly aporte?: AporteCampaña;
  readonly readOnly?: boolean;
}
interface SolicitudEntrega {
  readonly aporteId: string;
  readonly monto: number;
  readonly solicitudId: string;
  readonly tipo: 'dinero' | 'objeto';
  readonly anterior: number;
  /** Se resuelve una vez: si el resultado es incierto, el reintento usa el mismo comprobante sin subirlo de nuevo. */
  readonly respaldo: RespaldoMovimiento | null;
}
/** El importe escrito si es positivo, con hasta dos decimales y no supera lo pendiente; si no, null. */
function importeDeEntrega(monto: string, pendiente: number): number | null {
  const valor = Number(monto);
  const valido = /^\d+(?:\.\d{1,2})?$/.test(monto) && Number.isFinite(valor) && valor > 0 && valor <= pendiente;
  return valido ? valor : null;
}
function mensajeDeErrorDeEntrega(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') return error.message;
  return 'No pudimos registrar la entrega.';
}
function ResumenDelCompromiso({ aporte, pendiente, esDinero }: {
  readonly aporte: AporteCampaña;
  readonly pendiente: number;
  readonly esDinero: boolean;
}) {
  return (
    <div className="mb-5 rounded-lg bg-slate-50 p-4 text-sm">
      <p>Comprometido: <strong>{formatCurrency(aporte.monto_comprometido)}</strong></p>
      <p>Entregado: <strong>{formatCurrency(aporte.monto_aportado)}</strong></p>
      <p>Pendiente: <strong>{formatCurrency(pendiente)}</strong></p>
      {!esDinero && <p className="mt-2">La entrega en especie se valora, pero no ingresa dinero al fondo.</p>}
    </div>
  );
}
function AccionesDeEntrega({ bloqueado, registroBloqueado, incierto, onCancelar }: {
  readonly bloqueado: boolean;
  readonly registroBloqueado: boolean;
  readonly incierto: boolean;
  readonly onCancelar: () => void;
}) {
  return (
    <div className="flex justify-end gap-3">
      <button type="button" disabled={bloqueado} onClick={onCancelar}
        className="rounded-lg border px-4 py-2 disabled:opacity-50">Cancelar</button>
      <button type="submit" disabled={registroBloqueado}
        className="rounded-lg bg-emerald-600 px-4 py-2 text-white disabled:opacity-50">
        {incierto ? 'Verificar entrega' : 'Registrar entrega'}
      </button>
    </div>
  );
}
function Formulario({ aporte, onClose, onSuccess, readOnly = false }: Omit<Props, 'isOpen' | 'aporte'> & {
  readonly aporte: AporteCampaña;
}) {
  const [monto, setMonto] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const solicitud = useRef<SolicitudEntrega | null>(null);
  const [incierto, setIncierto] = useState(false);
  const [historialBloqueado, setHistorialBloqueado] = useState(false);
  const pendiente = importePendienteCampana(aporte.monto_comprometido, aporte.monto_aportado);
  const esDinero = aporte.tipo_aporte === 'dinero';

  // La entrega en especie es una valoración, sin dinero: no lleva comprobante.
  const [entidad, setEntidad] = useState<EntidadDinero | null>(null);
  useEffect(() => {
    if (!esDinero || readOnly) return;
    let vigente = true;
    comprobanteService.getEntidadDeCampana(aporte.campaña_id)
      .then((dueña) => { if (vigente) setEntidad(dueña); })
      .catch((err: unknown) => { if (vigente) reportarErrorInesperado(err, 'No pudimos preparar el comprobante del pago.'); });
    return () => { vigente = false; };
  }, [esDinero, readOnly, aporte.campaña_id]);
  // La base registra el cobro con la fecha de hoy en Chile: no hay campo de fecha.
  const [hoy] = useState(() => fechaEnChile(new Date()));
  const comprobante = useComprobanteFormulario({
    entidad,
    fechaMovimiento: hoy,
    permiteSubirDespues: true,
    pideMedioPago: esDinero,
    sentidoDinero: 'ingreso',
    obligatoriedad: esDinero ? 'desde_vigencia' : 'nunca',
  });

  const bloqueado = submitting || incierto || historialBloqueado;
  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  /** Sube el comprobante una sola vez; si falla, el mensaje queda en el formulario y no se registra nada. */
  const resolverRespaldo = async (): Promise<RespaldoMovimiento | null> => {
    try {
      return await comprobante.resolver();
    } catch (error_: unknown) {
      const deNegocio = mensajeErrorDeNegocio(error_);
      if (deNegocio) setError(deNegocio);
      else reportarErrorInesperado(error_, 'No pudimos subir el comprobante. Por favor intenta nuevamente.');
      return null;
    }
  };

  const registrar = async (event: FormEvent) => {
    event.preventDefault();
    if (readOnly || historialBloqueado) return;
    let actual = solicitud.current;
    if (!actual) {
      const valor = importeDeEntrega(monto, pendiente);
      if (valor === null) {
        setError('Indica un importe positivo, con hasta dos decimales, que no supere lo pendiente.');
        return;
      }
      const errorComprobante = esDinero ? comprobante.validar() : null;
      if (errorComprobante) { setError(errorComprobante); return; }
      actual = { aporteId: aporte.id, monto: valor, solicitudId: crypto.randomUUID(),
        tipo: aporte.tipo_aporte, anterior: aporte.monto_aportado, respaldo: null };
      solicitud.current = actual;
    }
    setSubmitting(true);
    setError('');
    try {
      if (actual.tipo === 'objeto') {
        await aporteCampanaService.registrarEntregaObjeto(actual.aporteId, actual.anterior, sumarImportesCampana(actual.anterior, actual.monto));
      } else {
        const respaldo = actual.respaldo ?? await resolverRespaldo();
        if (!respaldo) { solicitud.current = null; return; }
        actual = { ...actual, respaldo };
        solicitud.current = actual;
        await aporteCampanaService.registrarPago(actual.aporteId, actual.monto, actual.solicitudId, respaldo);
      }
      solicitud.current = null;
      setIncierto(false);
      comprobante.reiniciar();
      onSuccess();
      onClose();
    } catch (error_: unknown) {
      const desconocido = esResultadoCampanaIncierto(error_);
      setIncierto(desconocido);
      if (!desconocido) solicitud.current = null;
      setError(mensajeDeErrorDeEntrega(error_));
    } finally { setSubmitting(false); }
  };
  return (
    <DialogShell isOpen onClose={onClose} title="Entregas del compromiso" size="lg"
      icon={<DollarSign className="h-5 w-5" />} loading={bloqueado}
      hasUnsavedChanges={!readOnly && comprobante.hayCambios} closeRef={closeRef}>
      <ResumenDelCompromiso aporte={aporte} pendiente={pendiente} esDinero={esDinero} />
      {!readOnly && (pendiente > 0 || incierto) && <form onSubmit={registrar} className="space-y-4">
        <FormErrorAlert error={error} />
        {esDinero && <ComprobanteInput comprobante={comprobante} deshabilitado={bloqueado} />}
        <TextInput label="Valor de la entrega" value={monto} onChange={setMonto} type="number"
          min={0.01} max={pendiente} step={0.01} disabled={bloqueado} required />
        {esDinero && <MedioPagoFolio comprobante={comprobante} deshabilitado={bloqueado} />}
        {incierto && <p className="text-sm text-amber-800">Verifica la misma entrega antes de registrar otra. Se conservan su importe y solicitud.</p>}
        <AccionesDeEntrega bloqueado={bloqueado} registroBloqueado={submitting || historialBloqueado || comprobante.subiendo}
          incierto={incierto} onCancelar={requestClose} />
      </form>}
      <HistorialPagosCampana aporteId={aporte.id} readOnly={readOnly || submitting || incierto} entidad={entidad}
        onCambio={onSuccess} onBloqueo={setHistorialBloqueado} />
    </DialogShell>
  );
}
export function AporteCampanaModal({ isOpen, aporte, ...props }: Props) {
  if (!isOpen || !aporte) return null;
  return <Formulario key={aporte.id} aporte={aporte} {...props} />;
}
